import { Upload, X } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Button } from "../../button";
import { Label } from "@tellescope/design-system";
import { Progress } from "../../progress";
import { useImageUploadField } from "./useImageUploadField";

export interface ImageUploadFieldProps {
  /** The current image URL value ("" when no image is set) */
  value: string;
  /** Called with the new URL after a successful upload, or "" when the image is removed */
  onChange: (url: string) => void;
  /** Uploads the selected file and resolves to its public URL */
  onUpload: (file: File) => Promise<string>;
  /** Optional label rendered above the field */
  label?: string;
  /** id associating the label with the file input */
  id?: string;
  /** Accepted file types for the file input (default: common image formats) */
  accept?: string;
  /** Upload progress (0-100). When provided, shows a determinate progress bar while uploading */
  progress?: number;
  /** Whether the preview and the action buttons sit side by side ("row") or stacked ("column"). Default: "row" */
  orientation?: "row" | "column";
  /** Text for the upload button when no image is set (default: "Upload image") */
  uploadLabel?: string;
  /** Text for the upload button when an image is already set (default: "Replace image") */
  replaceLabel?: string;
  /** Text for the upload button while an upload is in progress (default: "Uploading…") */
  uploadingLabel?: string;
  /** Accessible label for the remove button (default: "Remove image") */
  removeLabel?: string;
  /** When true, the upload button stretches to fill the available width */
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
  labelClassName?: string;
}

export const ImageUploadField = ({
  value,
  onChange,
  onUpload,
  label,
  id,
  accept = ".png,.jpg,.jpeg,.gif",
  progress,
  orientation = "row",
  uploadLabel,
  replaceLabel,
  uploadingLabel,
  removeLabel,
  fullWidth = false,
  disabled = false,
  className,
  labelClassName,
}: ImageUploadFieldProps) => {
  const {
    t,
    fileInputRef,
    isDragging,
    isUploading,
    error,
    hasImage,
    handleInputChange,
    openFilePicker,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRemove,
  } = useImageUploadField({ value, onChange, onUpload, disabled });

  return (
    <div className={cn("space-y-2 overflow-hidden", className)}>
      {label && (
        <Label htmlFor={id} className={labelClassName}>
          {label}
        </Label>
      )}

      <input
        ref={fileInputRef}
        id={id}
        type="file"
        accept={accept}
        className="sr-only"
        disabled={disabled || isUploading}
        onChange={handleInputChange}
      />

      <div className={cn("flex gap-3", orientation === "row" ? "items-center" : "flex-col items-start")}>
        {/* Preview only renders once an image has been selected */}
        {hasImage && (
          <div className="relative h-16 w-16 shrink-0 rounded-md border border-border bg-muted">
            <img src={value} alt={label ?? t("Uploaded image")} className="h-full w-full object-contain" />
            <Button
              type="button"
              variant="destructive"
              size="sm-icon"
              disabled={disabled || isUploading}
              onClick={handleRemove}
              aria-label={removeLabel ?? t("Remove image")}
              className="absolute -right-1.5 -top-1.5 h-4 w-4 shadow-sm"
            >
              <X className="size-3" />
            </Button>
          </div>
        )}

        {/* Looks like a normal button, but also accepts a dropped image */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || isUploading}
          onClick={openFilePicker}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            isDragging && "border-primary ring-2 ring-ring",
            fullWidth && (orientation === "row" ? "flex-1" : "w-full")
          )}
        >
          <Upload className={cn("h-4 w-4", isUploading && "animate-pulse")} />
          {isUploading
            ? uploadingLabel ?? t("Uploading…")
            : hasImage
            ? replaceLabel ?? t("Replace image")
            : uploadLabel ?? t("Upload image")}
        </Button>
      </div>

      {isUploading && progress !== undefined && <Progress value={progress} className="h-1.5" />}

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};
